import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { consumeAuth } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | already | invalid
  const fired = useRef(false);

  // Same-site relative path only — never trust an absolute URL in the
  // verification link (the link goes through email, so we treat the
  // `next` param as untrusted input). Anything that doesn't start with
  // a single `/` is dropped.
  const rawNext = params.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("invalid"); return; }
    if (fired.current) return;
    fired.current = true;
    api.post("/auth/verify-email", { token })
      .then((r) => {
        if (r.data.token && r.data.user) {
          consumeAuth(r.data.token, r.data.user);
        }
        if (next) {
          navigate(next, { replace: true });
          return;
        }
        setState(r.data.already_verified ? "already" : "success");
      })
      .catch(() => setState("invalid"));
  }, [params, consumeAuth, navigate, next]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <p className="overline text-berulia-rosedeep">Confirmation</p>
      <h1 className="font-heading text-4xl md:text-5xl mt-3">
        {state === "success" && <>Bienvenue chez <span className="font-serif-italic">BERULIA</span></>}
        {state === "already" && <>Déjà <span className="font-serif-italic">confirmé</span></>}
        {state === "invalid" && <>Lien <span className="font-serif-italic">invalide</span></>}
        {state === "loading" && <>Un instant…</>}
      </h1>

      <div className="mt-10 bg-white border border-berulia-border p-8">
        {state === "loading" && (
          <p className="text-berulia-inkmuted italic">Vérification en cours…</p>
        )}
        {state === "success" && (
          <>
            <p className="text-berulia-inkdeep leading-relaxed">Votre adresse e-mail est confirmée ✿</p>
            <button
              onClick={() => navigate("/mon-compte")}
              className="mt-6 bg-berulia-rosedeep text-berulia-inkdeep overline py-3 px-6 hover:bg-berulia-rose"
            >
              Accéder à mon compte →
            </button>
          </>
        )}
        {state === "already" && (
          <>
            <p className="text-berulia-inkdeep leading-relaxed">Votre compte est déjà actif.</p>
            <Link to="/connexion" className="mt-6 inline-block overline link-underline text-berulia-rosedeep">
              Se connecter →
            </Link>
          </>
        )}
        {state === "invalid" && (
          <>
            <p className="text-berulia-inkdeep leading-relaxed">
              Ce lien est expiré ou invalide. Demandez un nouvel envoi depuis la page de connexion.
            </p>
            <Link to="/connexion" className="mt-6 inline-block overline link-underline text-berulia-rosedeep">
              Retour à la connexion →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
